
import React, { useState, createContext, useContext, ReactNode } from 'react';
import TactileConfirm from '../components/TactileConfirm';

interface ConfirmContextType {
  confirm: (title: string, message: string) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (context === undefined) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
};

interface ConfirmProviderProps {
  children: ReactNode;
}

export const ConfirmProvider: React.FC<ConfirmProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [resolver, setResolver] = useState<(value: boolean) => void>(() => {});

  const confirm = (title: string, message: string): Promise<boolean> => {
    setTitle(title);
    setMessage(message);
    setIsOpen(true);
    return new Promise<boolean>((resolve) => {
      setResolver(() => resolve);
    });
  };

  const handleConfirm = () => {
    resolver(true);
    setIsOpen(false);
  };

  const handleCancel = () => {
    resolver(false);
    setIsOpen(false);
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <TactileConfirm
        isOpen={isOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title={title}
        message={message}
      />
    </ConfirmContext.Provider>
  );
};
